import type { GetSoundboardResponse } from "@/app/_api/soundboards";

import { type User } from "firebase/auth";

import type { MidiDevice, PresetCategory } from "../types";
import type { SerializedState, State } from "./reducer";

import { type ApiPreset, KEYBOARD_MAPPING, type Sound } from "../types";

export interface SoundMetadata {
  currentTime?: number;
  duration: number;
  isLoaded: boolean;
}

export const MAX_SOUND_SLOTS = 16;

export enum PanelType {
  PRESET_EDITOR = "presetEditor",
  SOUND_EFFECT_EDITOR = "soundEffectEditor",
}

export enum ActionTypes {
  ACTIVATE_PRESET = "activatePreset",
  ADD_PRESET = "addPreset",
  ADD_ROW = "addRow",
  ADD_SOUND = "addSound",
  CLEAR_SOUND = "clearSound",
  CLOSE_PANEL = "closePanel",
  CREATE_SOUNDBOARD_SUCCESS = "createSoundboardSuccess",
  DELETE_LOCAL_PRESET = "deleteLocalPreset",
  DELETE_PRESET_SUCCESS = "deletePresetSuccess",
  LOAD_CUSTOM_SOUNDBOARD_SUCCESS = "loadCustomSoundboardSuccess",
  LOAD_MY_SOUNDBOARDS_START = "loadMySoundboardsStart",
  LOAD_MY_SOUNDBOARDS_SUCCESS = "loadMySoundboardsSuccess",
  LOAD_PRESETS = "loadPresets",
  LOAD_SAVED_STATE_FROM_SESSION_COOKIE = "loadSavedStateFromSessionCookie",
  MIDI_DEVICE_CONNECTED = "midiDeviceConnected",
  MIDI_DEVICE_DISCONNECTED = "midiDeviceDisconnected",
  MIDI_DEVICES_DETECTED = "midiDevicesDetected",
  MIDI_DISABLED = "midiDisabled",
  MIDI_ENABLED = "midiEnabled",
  MIDI_SET_ACTIVE_DEVICE = "midiSetActiveDevice",
  OPEN_PANEL = "openPanel",
  QUEUE_SOUND = "queueSound",
  RESET_PRESET = "resetPreset",
  SET_ACTIVE_PRESET_CATEGORY = "setActivePresetCategory",
  SET_VOLUME = "SET_VOLUME",
  SOUND_ENDED = "soundEnded",
  SOUND_LOADED = "soundLoaded",
  SOUND_PROGRESS = "soundProgress",
  SOUND_STARTED = "soundStarted",
  TOGGLE_EDIT_MODE = "toggleEditMode",
  TOGGLE_IS_PAUSED = "toggleIsPaused",
  TOGGLE_LOOP_EDIT_MODE = "toggleLoopEditMode",
  TOGGLE_SOUND_LOOP = "toggleSoundLoop",
  UPDATE_MIDI_MAPPING = "updateMidiMapping",
  UPDATE_PRESET = "updatePreset",
  UPDATE_SOUND_NAME = "updateSoundName",
  UPDATE_SOUNDBOARD_SUCCESS = "updateSoundboardSuccess",
  USER_STATE_CHANGED = "userStateChanged",
}

function createAction<T extends ActionTypes, P>(type: T) {
  return (payload: P) => ({ type, payload });
}

export const loadPresets = createAction<ActionTypes.LOAD_PRESETS, ApiPreset[]>(ActionTypes.LOAD_PRESETS);
export const activatePreset = createAction<ActionTypes.ACTIVATE_PRESET, string>(ActionTypes.ACTIVATE_PRESET);
export const addPreset = createAction<ActionTypes.ADD_PRESET, void>(ActionTypes.ADD_PRESET);
export const updatePreset = createAction<ActionTypes.UPDATE_PRESET, { icon: string; id: string; name: string }>(
  ActionTypes.UPDATE_PRESET,
);
export const queueSound = createAction<ActionTypes.QUEUE_SOUND, { presetId: string; soundId: string }>(
  ActionTypes.QUEUE_SOUND,
);
export const soundStarted = createAction<ActionTypes.SOUND_STARTED, { soundId: string }>(ActionTypes.SOUND_STARTED);
export const soundEnded = createAction<ActionTypes.SOUND_ENDED, { soundId: string }>(ActionTypes.SOUND_ENDED);
export const soundLoaded = createAction<ActionTypes.SOUND_LOADED, { duration: number; soundId: string }>(
  ActionTypes.SOUND_LOADED,
);
export const soundProgress = createAction<ActionTypes.SOUND_PROGRESS, { currentTime: number; soundId: string }>(
  ActionTypes.SOUND_PROGRESS,
);
export const setVolume = createAction<ActionTypes.SET_VOLUME, { volume: number }>(ActionTypes.SET_VOLUME);
export const toggleIsPaused = createAction<ActionTypes.TOGGLE_IS_PAUSED, void>(ActionTypes.TOGGLE_IS_PAUSED);
// Sound slot management actions
export const addSound = createAction<ActionTypes.ADD_SOUND, { presetId: string; slotIndex: number; sound: Sound }>(
  ActionTypes.ADD_SOUND,
);
export const clearSound = createAction<ActionTypes.CLEAR_SOUND, { presetId: string; slotIndex: number }>(
  ActionTypes.CLEAR_SOUND,
);
export const updateSoundName = createAction<
  ActionTypes.UPDATE_SOUND_NAME,
  { presetId: string; slotIndex: number; soundName: string }
>(ActionTypes.UPDATE_SOUND_NAME);
export const toggleEditMode = createAction<ActionTypes.TOGGLE_EDIT_MODE, void>(ActionTypes.TOGGLE_EDIT_MODE);
export const addRow = createAction<ActionTypes.ADD_ROW, void>(ActionTypes.ADD_ROW);
export const resetPreset = createAction<ActionTypes.RESET_PRESET, { presetId: string }>(ActionTypes.RESET_PRESET);
export const createSoundboardSuccess = createAction<
  ActionTypes.CREATE_SOUNDBOARD_SUCCESS,
  { existingPresetId?: string; soundboard: GetSoundboardResponse }
>(ActionTypes.CREATE_SOUNDBOARD_SUCCESS);
export const loadCustomSoundboardSuccess = createAction<
  ActionTypes.LOAD_CUSTOM_SOUNDBOARD_SUCCESS,
  { soundboard: GetSoundboardResponse }
>(ActionTypes.LOAD_CUSTOM_SOUNDBOARD_SUCCESS);
export const updateSoundboardSuccess = createAction<
  ActionTypes.UPDATE_SOUNDBOARD_SUCCESS,
  { soundboard: GetSoundboardResponse }
>(ActionTypes.UPDATE_SOUNDBOARD_SUCCESS);
export const loadMySoundboardsStart = createAction<ActionTypes.LOAD_MY_SOUNDBOARDS_START, void>(
  ActionTypes.LOAD_MY_SOUNDBOARDS_START,
);
export const loadMySoundboardsSuccess = createAction<
  ActionTypes.LOAD_MY_SOUNDBOARDS_SUCCESS,
  { soundboards: GetSoundboardResponse[] }
>(ActionTypes.LOAD_MY_SOUNDBOARDS_SUCCESS);
export const toggleSoundLoop = createAction<ActionTypes.TOGGLE_SOUND_LOOP, { soundId: string }>(
  ActionTypes.TOGGLE_SOUND_LOOP,
);
export const toggleLoopEditMode = createAction<ActionTypes.TOGGLE_LOOP_EDIT_MODE, void>(
  ActionTypes.TOGGLE_LOOP_EDIT_MODE,
);
export const userStateChanged = createAction<ActionTypes.USER_STATE_CHANGED, { user: User | null }>(
  ActionTypes.USER_STATE_CHANGED,
);
export const setActivePresetCategory = createAction<
  ActionTypes.SET_ACTIVE_PRESET_CATEGORY,
  { category: PresetCategory }
>(ActionTypes.SET_ACTIVE_PRESET_CATEGORY);
export const loadSavedStateFromSessionCookie = createAction<
  ActionTypes.LOAD_SAVED_STATE_FROM_SESSION_COOKIE,
  { state: SerializedState }
>(ActionTypes.LOAD_SAVED_STATE_FROM_SESSION_COOKIE);
export const deleteLocalPreset = createAction<ActionTypes.DELETE_LOCAL_PRESET, { presetId: string }>(
  ActionTypes.DELETE_LOCAL_PRESET,
);
export const deletePresetSuccess = createAction<ActionTypes.DELETE_PRESET_SUCCESS, { presetId: string }>(
  ActionTypes.DELETE_PRESET_SUCCESS,
);

// Panel management actions
export interface PanelState {
  data?: any;
  type: PanelType;
}

export const openPanel = createAction<ActionTypes.OPEN_PANEL, PanelState>(ActionTypes.OPEN_PANEL);
export const closePanel = createAction<ActionTypes.CLOSE_PANEL, void>(ActionTypes.CLOSE_PANEL);

// Helper functions for opening specific panels
export const openPresetEditor = (presetId: string) =>
  openPanel({
    data: { presetId },
    type: PanelType.PRESET_EDITOR,
  });

export interface SoundEffectEditorData {
  buttonIndex: number;
  id: string;
  soundName: null | string;
  soundUrl: null | string;
}

export const openSoundEffectEditor = (buttonIndex: number, state: State) => {
  const activePreset = state.presets[state.activePresetId!];
  const sound = activePreset?.sounds[buttonIndex];
  return openPanel({
    data: {
      buttonIndex,
      id: KEYBOARD_MAPPING[buttonIndex] ?? buttonIndex + 1,
      soundName: sound?.name,
      soundUrl: sound && "url" in sound ? sound.url : null,
    },
    type: PanelType.SOUND_EFFECT_EDITOR,
  });
};

export const midiEnabled = createAction<ActionTypes.MIDI_ENABLED, void>(ActionTypes.MIDI_ENABLED);

export const midiDisabled = createAction<ActionTypes.MIDI_DISABLED, { error?: string }>(ActionTypes.MIDI_DISABLED);

export const midiDevicesDetected = createAction<ActionTypes.MIDI_DEVICES_DETECTED, { devices: MidiDevice[] }>(
  ActionTypes.MIDI_DEVICES_DETECTED,
);

export const midiDeviceConnected = createAction<ActionTypes.MIDI_DEVICE_CONNECTED, { device: MidiDevice }>(
  ActionTypes.MIDI_DEVICE_CONNECTED,
);

export const midiDeviceDisconnected = createAction<ActionTypes.MIDI_DEVICE_DISCONNECTED, { device: MidiDevice }>(
  ActionTypes.MIDI_DEVICE_DISCONNECTED,
);

export const midiSetActiveDevice = createAction<ActionTypes.MIDI_SET_ACTIVE_DEVICE, { deviceId: null | string }>(
  ActionTypes.MIDI_SET_ACTIVE_DEVICE,
);

export const updateMidiMapping = createAction<ActionTypes.UPDATE_MIDI_MAPPING, { mapping: Record<number, number> }>(
  ActionTypes.UPDATE_MIDI_MAPPING,
);

export type Action =
  | ReturnType<typeof activatePreset>
  | ReturnType<typeof addPreset>
  | ReturnType<typeof addRow>
  | ReturnType<typeof addSound>
  | ReturnType<typeof clearSound>
  | ReturnType<typeof closePanel>
  | ReturnType<typeof createSoundboardSuccess>
  | ReturnType<typeof deleteLocalPreset>
  | ReturnType<typeof deletePresetSuccess>
  | ReturnType<typeof loadCustomSoundboardSuccess>
  | ReturnType<typeof loadMySoundboardsStart>
  | ReturnType<typeof loadMySoundboardsSuccess>
  | ReturnType<typeof loadPresets>
  | ReturnType<typeof loadSavedStateFromSessionCookie>
  | ReturnType<typeof midiDeviceConnected>
  | ReturnType<typeof midiDeviceDisconnected>
  | ReturnType<typeof midiDevicesDetected>
  | ReturnType<typeof midiDisabled>
  | ReturnType<typeof midiEnabled>
  | ReturnType<typeof midiSetActiveDevice>
  | ReturnType<typeof openPanel>
  | ReturnType<typeof queueSound>
  | ReturnType<typeof resetPreset>
  | ReturnType<typeof setActivePresetCategory>
  | ReturnType<typeof setVolume>
  | ReturnType<typeof soundEnded>
  | ReturnType<typeof soundLoaded>
  | ReturnType<typeof soundProgress>
  | ReturnType<typeof soundStarted>
  | ReturnType<typeof toggleEditMode>
  | ReturnType<typeof toggleIsPaused>
  | ReturnType<typeof toggleLoopEditMode>
  | ReturnType<typeof toggleSoundLoop>
  | ReturnType<typeof updateMidiMapping>
  | ReturnType<typeof updatePreset>
  | ReturnType<typeof updateSoundName>
  | ReturnType<typeof updateSoundboardSuccess>
  | ReturnType<typeof userStateChanged>;
